/**
 * registerServiceWorker.ts — PWA service worker registration.
 *
 * Uses `virtual:pwa-register` (resolved at build time by vite-plugin-pwa) to
 * register the Workbox-generated service worker.
 *
 * UPDATE HANDLING IS DELIBERATELY MANUAL. `registerType: 'autoUpdate'` in
 * vite.config.ts does not, on its own, produce a self-activating worker here:
 * the generated sw.js skips waiting only when messaged (`SKIP_WAITING`), and
 * `onNeedRefresh` is never invoked in this configuration. Both were verified
 * against the deployed site — a new worker sat in `waiting` across repeated
 * reloads while the page kept serving the previous bundle. Anyone who had
 * loaded the app once was pinned to that build permanently, so deploys were
 * invisible to them.
 *
 * So we drive the lifecycle ourselves and depend on no plugin-mode semantics:
 * message any waiting worker to skip, watch for ones that install later, poll
 * for new deploys, and reload once the new worker takes control.
 *
 * TODO(ui-generator): Surface a user-visible "Update available — click to
 * reload" snackbar and gate `activate()` behind it, so a reload is never
 * forced mid-task. Until that exists, auto-applying is the lesser evil: the
 * alternative is a permanently stale app.
 */
import { registerSW } from 'virtual:pwa-register';

// Dev safety: unregister any pre-existing service worker (left over from a
// prior production build / preview session). A stale SW intercepts API
// requests and tries to cache them under StaleWhileRevalidate; with the
// .NET dev cert (untrusted in SW context), every fetch fails with
// "TypeError: Failed to fetch" and the app looks broken. Registering the
// dev SW is already a no-op (vite.config.ts devOptions.enabled = false),
// but we have to actively unregister anything left over from before.
if (import.meta.env.DEV && 'serviceWorker' in navigator) {
  navigator.serviceWorker.getRegistrations().then((regs) => {
    for (const r of regs) {
      r.unregister().then((ok) => {
        if (ok) console.log('[SW] Unregistered stale dev service worker:', r.scope);
      });
    }
  });
}

/** How often a long-lived tab asks the server whether a new build exists. */
const UPDATE_POLL_MS = 60_000;

/** Tell a waiting worker to take over. sw.js listens for exactly this message. */
function activate(registration: ServiceWorkerRegistration): void {
  registration.waiting?.postMessage({ type: 'SKIP_WAITING' });
}

// Reload once the new worker takes control. Guarded because controllerchange
// can fire more than once, and an unguarded reload here is a refresh loop.
if ('serviceWorker' in navigator) {
  let reloading = false;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (reloading) return;
    reloading = true;
    window.location.reload();
  });
}

registerSW({
  // Deliberately the only registration callback: supplying the older
  // `onRegistered` alongside it makes which one runs ambiguous.
  onRegisteredSW(_swUrl, registration) {
    if (!registration) return;
    if (import.meta.env.DEV) console.log('[SW] Registered:', registration.scope);

    // A worker may already be waiting from a previous visit.
    activate(registration);

    // ...and one may finish installing later in this session.
    registration.addEventListener('updatefound', () => {
      const installing = registration.installing;
      if (!installing) return;
      installing.addEventListener('statechange', () => {
        if (installing.state === 'installed') activate(registration);
      });
    });

    // Without this, a tab left open all day never learns about a deploy —
    // the browser only re-checks sw.js on navigation.
    setInterval(() => void registration.update(), UPDATE_POLL_MS);
  },
  onOfflineReady() {
    // The app is now fully cached and can work offline.
    if (import.meta.env.DEV) console.log('[SW] App is ready to work offline.');
  },
  onRegisterError(error) {
    console.error('[SW] Service worker registration failed:', error);
  },
});
